import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Mail, Lock, User, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const loginSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
});

const signupSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
  confirmPassword: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
  fullName: z.string().min(2, 'Le nom doit contenir au moins 2 caractères'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"],
});

const resetSchema = z.object({
  email: z.string().email('Email invalide'),
});

type LoginForm = z.infer<typeof loginSchema>;
type SignupForm = z.infer<typeof signupSchema>;
type ResetForm = z.infer<typeof resetSchema>;

interface AuthFormProps {
  onSuccess?: () => void;
}

interface Notification {
  type: 'success' | 'error';
  message: string;
}

export default function AuthForm({ onSuccess }: AuthFormProps) {
  const [mode, setMode] = useState<'login' | 'signup' | 'reset'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<Notification | null>(null);
  const { signIn, signUp, resetPassword } = useAuth();

  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const signupForm = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
  });

  const resetForm = useForm<ResetForm>({
    resolver: zodResolver(resetSchema),
  });

  const getErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
      // Messages d'erreur Supabase personnalisés
      switch (error.message) {
        case 'Invalid login credentials':
          return 'Email ou mot de passe incorrect. Vérifiez vos identifiants.';
        case 'Email not confirmed':
          return 'Veuillez confirmer votre email avant de vous connecter. Vérifiez votre boîte de réception et vos spams.';
        case 'User not found':
          return 'Aucun compte associé à cet email. Créez un compte d\'abord.';
        case 'Too many requests':
          return 'Trop de tentatives. Veuillez patienter avant de réessayer.';
        case 'User already registered':
          return 'Cet email est déjà utilisé. Connectez-vous ou utilisez un autre email.';
        case 'Password should be at least 6 characters':
          return 'Le mot de passe doit contenir au moins 6 caractères.';
        case 'Signup is disabled':
          return 'Les inscriptions sont temporairement désactivées.';
        case 'Email link is invalid or has expired':
          return 'Le lien de vérification a expiré. Veuillez demander un nouveau lien.';
        case 'One-time token not found':
          return 'Le lien de vérification est invalide ou a expiré. Veuillez demander un nouveau lien.';
        default:
          return error.message;
      }
    }
    return 'Une erreur inattendue est survenue.';
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    // Auto-hide après 5 secondes pour les messages de succès
    if (type === 'success') {
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const hideNotification = () => {
    setNotification(null);
  };

  const handleLogin = async (data: LoginForm) => {
    setLoading(true);
    setNotification(null);
    try {
      const { error } = await signIn(data.email, data.password);
      if (error) {
        showNotification('error', getErrorMessage(error));
      } else {
        showNotification('success', 'Connexion réussie !');
        setTimeout(() => {
          onSuccess?.();
        }, 1000);
      }
    } catch (error) {
      showNotification('error', 'Une erreur de connexion est survenue.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (data: SignupForm) => {
    setLoading(true);
    setNotification(null);
    try {
      const { error } = await signUp(data.email, data.password, data.fullName);
      if (error) {
        showNotification('error', getErrorMessage(error));
      } else {
        showNotification('success', 'Compte créé ! Vérifiez votre email pour confirmer votre inscription.');
        setTimeout(() => {
          changeMode('login');
        }, 3000);
      }
    } catch (error) {
      showNotification('error', 'Une erreur est survenue lors de la création du compte.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (data: ResetForm) => {
    setLoading(true);
    setNotification(null);
    try {
      const { error } = await resetPassword(data.email);
      if (error) {
        showNotification('error', getErrorMessage(error));
      } else {
        showNotification('success', 'Si cet email existe dans notre base de données, vous recevrez un lien de réinitialisation dans votre boîte de réception. Vérifiez également vos spams.');
        setTimeout(() => {
          changeMode('login');
        }, 5000); // Plus de temps pour lire le message
      }
    } catch (error) {
      showNotification('error', 'Une erreur est survenue lors de l\'envoi de l\'email.');
    } finally {
      setLoading(false);
    }
  };

  const changeMode = (newMode: 'login' | 'signup' | 'reset') => {
    setMode(newMode);
    setNotification(null);
  };

  return (
    <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-8 max-w-md mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {mode === 'login' && 'Connexion'}
          {mode === 'signup' && 'Créer un compte'}
          {mode === 'reset' && 'Réinitialiser le mot de passe'}
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          {mode === 'login' && 'Connectez-vous à votre espace membre'}
          {mode === 'signup' && 'Rejoignez-nous dès aujourd\'hui'}
          {mode === 'reset' && 'Entrez votre email pour recevoir un lien de réinitialisation'}
        </p>
      </div>

      {mode === 'login' && (
        <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-white  mb-2">
              Email :
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                {...loginForm.register('email')}
                type="email"
                className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder:text-white"
                placeholder="votre@email.com"
              />
            </div>
            {loginForm.formState.errors.email && (
              <p className="mt-1 text-sm text-red-600">{loginForm.formState.errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white  mb-2">
              Mot de passe :
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                {...loginForm.register('password')}
                type={showPassword ? 'text' : 'password'}
                className="w-full pl-10 pr-12 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder:text-white"
                placeholder="Votre mot de passe"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {loginForm.formState.errors.password && (
              <p className="mt-1 text-sm text-red-600">{loginForm.formState.errors.password.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>

          {notification && (
            <div className={`mt-4 p-4 rounded-xl border-2 ${
              notification.type === 'success' 
                ? 'bg-green-50 border-green-200 text-green-800' 
                : 'bg-red-50 border-red-200 text-red-800'
            } relative`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  {notification.type === 'success' ? (
                    <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                  )}
                  <span className="text-sm font-medium">{notification.message}</span>
                </div>
                <button
                  onClick={hideNotification}
                  className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </form>
      )}

      {mode === 'signup' && (
        <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-white  mb-2">
              Nom complet :
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                {...signupForm.register('fullName')}
                type="text"
                className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder:text-white"
                placeholder="Votre nom complet"
              />
            </div>
            {signupForm.formState.errors.fullName && (
              <p className="mt-1 text-sm text-red-600">{signupForm.formState.errors.fullName.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white  mb-2">
              Email :
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                {...signupForm.register('email')}
                type="email"
                className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder:text-white"
                placeholder="votre@email.com"
              />
            </div>
            {signupForm.formState.errors.email && (
              <p className="mt-1 text-sm text-red-600">{signupForm.formState.errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white  mb-2">
              Mot de passe :
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                {...signupForm.register('password')}
                type={showPassword ? 'text' : 'password'}
                className="w-full pl-10 pr-12 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder:text-white"
                placeholder="Votre mot de passe"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {signupForm.formState.errors.password && (
              <p className="mt-1 text-sm text-red-600">{signupForm.formState.errors.password.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white  mb-2">
              Confirmer le mot de passe :
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                {...signupForm.register('confirmPassword')}
                type={showConfirmPassword ? 'text' : 'password'}
                className="w-full pl-10 pr-12 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder:text-white"
                placeholder="Confirmez votre mot de passe"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {signupForm.formState.errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{signupForm.formState.errors.confirmPassword.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Création...' : 'Créer le compte'}
          </button>

          {notification && (
            <div className={`mt-4 p-4 rounded-xl border-2 ${
              notification.type === 'success' 
                ? 'bg-green-50 border-green-200 text-green-800' 
                : 'bg-red-50 border-red-200 text-red-800'
            } relative`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  {notification.type === 'success' ? (
                    <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                  )}
                  <span className="text-sm font-medium">{notification.message}</span>
                </div>
                <button
                  onClick={hideNotification}
                  className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </form>
      )}

      {mode === 'reset' && (
        <form onSubmit={resetForm.handleSubmit(handleReset)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-white  mb-2">
              Email :
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                {...resetForm.register('email')}
                type="email"
                className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder:text-white"
                placeholder="votre@email.com"
              />
            </div>
            {resetForm.formState.errors.email && (
              <p className="mt-1 text-sm text-red-600">{resetForm.formState.errors.email.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Envoi...' : 'Envoyer le lien de réinitialisation'}
          </button>

          {notification && (
            <div className={`mt-4 p-4 rounded-xl border-2 ${
              notification.type === 'success' 
                ? 'bg-green-50 border-green-200 text-green-800' 
                : 'bg-red-50 border-red-200 text-red-800'
            } relative`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  {notification.type === 'success' ? (
                    <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                  )}
                  <span className="text-sm font-medium">{notification.message}</span>
                </div>
                <button
                  onClick={hideNotification}
                  className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </form>
      )}

      <div className="mt-8 text-center space-y-4">
        {mode === 'login' && (
          <>
            <button
              onClick={() => changeMode('reset')}
              className="text-purple-600 hover:text-purple-700 text-sm"
            >
              Mot de passe oublié ?
            </button>
            <div>
              <span className="text-white ">Pas encore de compte ? </span>
              <button
                onClick={() => changeMode('signup')}
                className="text-purple-600 hover:text-purple-700 font-medium"
              >
                Créer un compte
              </button>
            </div>
          </>
        )}

        {mode === 'signup' && (
          <div>
            <span className="text-white ">Déjà un compte ? </span>
            <button
              onClick={() => changeMode('login')}
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Se connecter
            </button>
          </div>
        )}

        {mode === 'reset' && (
          <button
            onClick={() => changeMode('login')}
            className="text-purple-600 hover:text-purple-700 text-sm"
          >
            Retour à la connexion
          </button>
        )}
      </div>
    </div>
  );
}
